import type { CSSProperties } from 'react';
import type { meetupSection } from '../../../../app/_shared/marketing/landingContent';
import {
  MEETUP_PLAN_SCREENSHOT,
  MEETUP_SAFETY_SCREENSHOT,
  MEETUP_TIMES_SCREENSHOT,
} from '../../assets/marketing/app-screenshots/appScreenshotsExport';
import { AppScreenshot } from './AppScreenshot';
import { withEmphasis } from './emphasisText';
import './problemAndWhy.css';

type Item = (typeof meetupSection.items)[number];

type Props = {
  eyebrow: string;
  h2: string;
  lead: string;
  items: readonly Item[];
  venueChips: readonly string[];
  safetyRowLabel: string;
  safetyNote: string;
  closer: string;
};

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden>
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.25" />
      <path
        d="M5 8.2 7 10.2 11 6.2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The meet-up beat. Read the claims block on `meetupSection` before editing the copy — the notice
 * line in particular is scoped to "the group" on purpose, and a backend test pins that it is never
 * attributed to a person.
 *
 * The chips-and-safety visual keeps its original `beyond-circle-section__*` class names even
 * though it no longer lives in that section: those rules are standalone selectors in
 * marketingProduct.css, not descendants of `.beyond-circle-section`, so they travel intact. Moving
 * the markup and renaming the classes at the same time would have been two changes to verify
 * instead of one.
 */
export function MeetupSection({
  eyebrow,
  h2,
  lead,
  items,
  venueChips,
  safetyRowLabel,
  safetyNote,
  closer,
}: Props) {
  return (
    <section className="stmt-section" aria-labelledby="meetup-heading">
      <header className="stmt-section__head">
        <p className="stmt-section__eyebrow">{eyebrow}</p>
        <h2 id="meetup-heading" className="ml-heading-serif ml-text-heading stmt-section__title">
          {h2}
        </h2>
        <p className="stmt-section__lead">{lead}</p>
      </header>

      <div className="stmt-grid">
        {items.map((item, index) => (
          <article
            key={item.id}
            className="stmt-item ml-revealStaggerItem"
            style={{ '--reveal-i': index } as CSSProperties}>
            <h3 className="ml-heading-sans stmt-item__title">{item.title}</h3>
            <p className="stmt-item__body">{withEmphasis(item.body)}</p>
          </article>
        ))}
      </div>

      {/*
        One crop per claim above, in the claims' own order: somebody offers times, enough people say
        yes to the same one, and the plan carries a check-in. This section made three assertions
        about a screen it never showed — every other section on the page shows the screen it is
        describing, and the chip strip below is a drawing, not the product.

        The plan leads because it is the payoff the other two exist for. Not `priority`: this sits
        well below the fold, where an eager fetch only competes with the hero.
      */}
      <div className="meetup-shots">
        <AppScreenshot shot={MEETUP_TIMES_SCREENSHOT} treatment="moment" width="dense" />
        <AppScreenshot shot={MEETUP_PLAN_SCREENSHOT} treatment="moment" width="lead" />
        <AppScreenshot shot={MEETUP_SAFETY_SCREENSHOT} treatment="moment" width="dense" />
      </div>

      <div className="meetup-visual">
        <div className="beyond-circle-section__irlVisual" aria-hidden>
          <div className="beyond-circle-section__venueChips">
            {venueChips.map((chip, idx) => (
              <span
                key={chip}
                className={`beyond-circle-section__venueChip${idx === 0 ? ' beyond-circle-section__venueChip--active' : ''}`}>
                {chip}
              </span>
            ))}
          </div>
          <p className="beyond-circle-section__safetyRow">
            <CheckIcon />
            {safetyRowLabel}
          </p>
          <p className="beyond-circle-section__safetyNote">{safetyNote}</p>
        </div>
      </div>

      <p className="stmt-section__closer">{withEmphasis(closer)}</p>
    </section>
  );
}
